import type { Side, StageCard, StageView } from '../types/stage';

export interface WallGeometry {
  mode: 'orbit' | 'stack';
  w: number;
  h: number;
  /** Anna's core */
  cx: number;
  cy: number;
  /** Visible radius of Anna's field in this view */
  R: number;
  /** Canvas side; the canvas is sized for full presence and scaled down per view */
  canvas: number;
  scale: number;
  cardW: number;
  /** Vertical band the orbit cards may use */
  top: number;
  bottom: number;
}

export interface Placement {
  /** Card centre, relative to Anna's core */
  x: number;
  y: number;
  scale: number;
  /** 0 = newest, in front */
  depth: number;
  side: Side;
  /** Where the filament meets the card (x relative to the core) */
  anchorX: number;
}

/** Visible sphere radius as a fraction of the canvas half-size (particleField rust radius). */
const FIELD = 0.62;
const EDGE = 40;
const MIN_CARD = 330;
const MAX_CARD = 460;
const GAP = 28;
const DEPTH_SCALE = [1, 0.95, 0.9];
/** Cards start beyond the satellite orbits. */
export const ORBIT_GAP = 1.3;

const VIEW: Record<StageView, { s: number; cy: number }> = {
  stage: { s: 1, cy: 0.46 },
  sleep: { s: 1, cy: 0.46 },
  alarm: { s: 0.5, cy: 0.24 },
  approvals: { s: 0.4, cy: 0.2 },
};

/** Anna fills the room: at 1920×1200 her field is 60 % of the screen height. */
export function wallGeometry(w: number, h: number, view: StageView): WallGeometry {
  const top = 150;
  const bottom = h - 140;
  if (w < 1024 || h < 600) {
    const R = Math.min(w * 0.34, h * 0.24);
    return { mode: 'stack', w, h, cx: w / 2, cy: 150 + R, R, canvas: (R * 2) / FIELD, scale: 1, cardW: Math.min(560, w - 32), top, bottom };
  }
  const Rfull = Math.max(150, Math.min(h * 0.3, (w / 2 - EDGE - MIN_CARD - 20) / ORBIT_GAP));
  const cardW = Math.max(MIN_CARD, Math.min(MAX_CARD, w / 2 - EDGE - (Rfull * ORBIT_GAP + 20)));
  const v = VIEW[view];
  const tight = h < 900 && view === 'approvals';
  const s = tight ? 0.32 : v.s;
  const cy = h * (tight ? 0.16 : v.cy);
  return { mode: 'orbit', w, h, cx: w / 2, cy, R: Rfull * s, canvas: (Rfull * 2) / FIELD, scale: s, cardW, top, bottom };
}

/**
 * Cards orbit Anna left and right. Newest is in front; older ones recede in depth.
 * Each side stacks around her core and stays inside the free band.
 */
export function layoutOrbit(cards: StageCard[], heights: Record<string, number>, g: WallGeometry): Record<string, Placement> {
  const out: Record<string, Placement> = {};
  (['links', 'rechts'] as Side[]).forEach((side) => {
    const list = cards.filter((c) => c.side === side);
    if (!list.length) return;
    const meta = list.map((c) => {
      const depth = cards.length - 1 - cards.indexOf(c);
      const scale = DEPTH_SCALE[depth] ?? DEPTH_SCALE[DEPTH_SCALE.length - 1];
      return { c, depth, scale, h: (heights[c.uid] ?? 260) * scale };
    });
    const total = meta.reduce((s, m) => s + m.h, 0) + GAP * (meta.length - 1);
    let y = Math.max(g.top - g.cy, Math.min(g.bottom - g.cy - total, -total / 2 - 16));
    const dir = side === 'links' ? -1 : 1;
    const inner = g.R * ORBIT_GAP + 20;
    meta.forEach((m) => {
      out[m.c.uid] = {
        x: dir * (inner + (g.cardW * m.scale) / 2),
        y: y + m.h / 2,
        scale: m.scale,
        depth: m.depth,
        side,
        anchorX: dir * inner,
      };
      y += m.h + GAP;
    });
  });
  return out;
}
